import React from 'react';
import { Target, Users, TrendingUp, Award, PlayCircle } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';

const About = () => {
  const values = [
    {
      icon: Target,
      title: 'Our Vision',
      description: 'We intend to become our customer\'s first choice at providing electrical & engineering solutions.'
    },
    {
      icon: Users,
      title: 'Team of Specialists',
      description: 'We rely on our team of specialists and advanced systems to monitor process, quality and delivery timelines.'
    },
    {
      icon: TrendingUp,
      title: 'Business Approach',
      description: 'Complete business solution with BTS planning, commissioning and execution. We provide activity briefs and quality control.'
    },
    {
      icon: Award,
      title: 'Quality Standards',
      description: 'We follow EHS practices and provide monthly and weekly project communication with effective schedules and budgets.'
    }
  ];

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Hero Section */}
      <section className="relative py-20 bg-slate-900 text-white">
        <div className="absolute inset-0 z-0">
          <img
            src="https://images.unsplash.com/photo-1559136555-9303baea8ebd?w=1200&q=75&auto=format"
            alt="Team collaboration"
            className="w-full h-full object-cover opacity-20"
            loading="lazy"
          />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 text-center">
          <h1 className="text-5xl md:text-6xl font-bold mb-6">About Hi-Tech Engineers</h1>
          <p className="text-xl text-slate-300 max-w-3xl mx-auto">
            35 years of excellence in electrical engineering solutions
          </p>
        </div>
      </section>

      {/* Company Overview */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl font-bold mb-6 text-slate-900">Who We Are</h2>
              <p className="text-lg text-slate-600 mb-6 leading-relaxed">
                Hi-Tech Engineers and Consultants is a complete electrical engineering solutions company. Operating for the last 35 years across India, we provide services to the government, private and corporate sectors.
              </p>
              <p className="text-lg text-slate-600 mb-6 leading-relaxed">
                Established in 1991, we have built a reputation for delivering high-quality engineering solutions with 100% client satisfaction. Our team has successfully completed projects for leading corporations including DLF, Tata Consultancy Services, Radisson, Emaar, Genpact, and Ireo across India.
              </p>
              <div className="grid grid-cols-2 gap-6 mt-8">
                <div className="text-center p-6 bg-blue-50 rounded-lg">
                  <div className="text-4xl font-bold text-blue-600 mb-2">1991</div>
                  <div className="text-slate-600">Year Established</div>
                </div>
                <div className="text-center p-6 bg-blue-50 rounded-lg">
                  <div className="text-4xl font-bold text-blue-600 mb-2">35</div>
                  <div className="text-slate-600">Years Experience</div>
                </div>
                <div className="text-center p-6 bg-blue-50 rounded-lg">
                  <div className="text-4xl font-bold text-blue-600 mb-2">50+</div>
                  <div className="text-slate-600">Clients</div>
                </div>
                <div className="text-center p-6 bg-blue-50 rounded-lg">
                  <div className="text-4xl font-bold text-blue-600 mb-2">100%</div>
                  <div className="text-slate-600">Satisfaction Rate</div>
                </div>
              </div>
            </div>
            <div>
              <img
                src="https://images.unsplash.com/photo-1685946973834-81f1c465ec0c?w=600&q=75&auto=format"
                alt="Modern office"
                className="rounded-lg shadow-2xl w-full h-[600px] object-cover"
                loading="lazy"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Video Showcase Section */}
      <section className="py-20 bg-slate-900">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold mb-4 text-white">See Our Work in Action</h2>
            <p className="text-xl text-slate-300">Watch our team delivering excellence in engineering solutions</p>
          </div>
          
          <div className="relative rounded-xl overflow-hidden shadow-2xl">
            <video 
              className="w-full h-auto"
              controls
              preload="metadata"
              poster="https://images.unsplash.com/photo-1487491424367-7571f9afbb30?w=1200&q=75&auto=format"
            >
              <source 
                src="https://customer-assets.emergentagent.com/job_ee7a6fc9-ec67-452c-823a-7c8341086989/artifacts/m6vek4fe_Hi%20Tech%20Engineers%20and%20Consultants%20Gurugram.mp4" 
                type="video/mp4" 
              />
              Your browser does not support the video tag.
            </video>
          </div>
        </div>
      </section>

      {/* Vision and Values */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-4 text-slate-900">Our Vision & Values</h2>
            <p className="text-xl text-slate-600">What drives us to deliver excellence every day</p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => {
              const Icon = value.icon;
              return (
                <Card key={index} className="border-none shadow-lg hover:shadow-xl transition-shadow duration-300">
                  <CardContent className="p-6">
                    <Icon className="h-12 w-12 text-blue-600 mb-4" />
                    <h3 className="text-xl font-bold mb-3 text-slate-900">{value.title}</h3>
                    <p className="text-slate-600">{value.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Awards Section */}
      <section className="py-20 bg-slate-900 text-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-4">Awards & Recognition</h2>
            <p className="text-xl text-slate-300">Honored for excellence in engineering and client satisfaction</p>
          </div>
          
          <div className="flex justify-center">
            <Card className="bg-slate-800 border-slate-700 max-w-md">
              <CardContent className="p-8 text-center">
                <div className="mb-6">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_ee7a6fc9-ec67-452c-823a-7c8341086989/artifacts/3lrkz54n_Logo%20-%20IMTCA%202019.jpg"
                    alt="India's Most Trusted Companies Award 2019"
                    className="w-64 h-64 mx-auto object-contain"
                    loading="lazy"
                  />
                </div>
                <h3 className="text-2xl font-bold mb-3 text-white">India's Most Trusted Companies</h3>
                <p className="text-slate-300 mb-2">Industry Validated Award</p>
                <p className="text-blue-400 font-semibold text-lg">2019</p>
                <p className="text-slate-400 text-sm mt-4">Recognized by Company's Research Report, IBC 2019</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Business Approach */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <img
                src="https://images.unsplash.com/photo-1609293241092-8c4e5cf64af8?w=600&q=75&auto=format"
                alt="Construction project"
                className="rounded-lg shadow-2xl w-full h-[500px] object-cover"
                loading="lazy"
              />
            </div>
            <div>
              <h2 className="text-4xl font-bold mb-6 text-slate-900">Our Business Approach</h2>
              <div className="space-y-4">
                <p className="text-lg text-slate-600 leading-relaxed">
                  We offer a complete business solution with BTS planning, commissioning and execution. We provide activity briefs and quality control and measure our performance targets versus client expectations.
                </p>
                <p className="text-lg text-slate-600 leading-relaxed">
                  We also provide monthly and weekly project communication and pride ourselves on our client care and feedback. We take great care in setting effective & accurate schedules, budgets & project interfaces.
                </p>
                <p className="text-lg text-slate-600 leading-relaxed">
                  For this, we rely on our team of specialists and our advanced systems to monitor process, quality and delivery timelines, all while following EHS practices.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;